'use client';

import { useTranslations } from 'next-intl';
import React from 'react';

import { ContentDto } from '@cineamo/api/models/content/ContentDto.types';

import { HrefOrHrefWithParams } from 'next-intl/dist/types/navigation/shared/utils';

import { loadAsset } from '@cineamo/ui/src/helpers/asset-helper';
import { cn } from '@cineamo/ui/src/helpers/cn';

import { CachedContentData } from '@/hooks/useCachedContentData';
import { useFormat } from '@cineamo/ui/src/hooks/formatter/useFormat';

import ImageWithTransition from '@cineamo/ui/src/image/ImageWithTransition';

import { useAnalytics } from '@cineamo/composables/src/providers/analyticsProvider';

import { Link } from '@/i18n/routing';
import { dateTodayPlus } from '@/utils/date-helper';
import { ComponentAnalytics, usePageName } from '@/utils/tracking-helper';

export type HorizontalMovieListItemProps = {
    content: CachedContentData;
    onMovieItemClick?: (movie: CachedContentData, index: number) => void;
    onMovieItemHover?: (movie: CachedContentData, index: number) => void;
    selectedContentItem?: CachedContentData;
    index: number;
    analytics?: ComponentAnalytics;
    showReleaseDate?: boolean;
};

function HorizontalMovieListItem(props: HorizontalMovieListItemProps): React.JSX.Element {
    const { content, selectedContentItem, onMovieItemClick, onMovieItemHover, index, analytics, showReleaseDate } =
        props;

    const { track } = useAnalytics();
    const t = useTranslations();
    const format = useFormat();

    const isActiveContentItem = content?.id === selectedContentItem?.id;

    const currentPageName = usePageName();

    const posterImageUrl = content.posterImage;
    const posterBlurImageUrl = content.posterBlurDataImage;
    const title = content.title;
    const releaseDate = content.releaseDate;

    // Validiere das Release Date
    const formattedReleaseDate = (() => {
        if (!releaseDate) return null;
        const date = new Date(releaseDate);
        return !isNaN(date.getTime()) ? date : null;
    })();

    function getProgramDetailUrl(contentId: ContentDto['id']): HrefOrHrefWithParams<string> {
        return {
            pathname: '/program/[contentId]',
            params: {
                contentId: contentId
            }
        } as HrefOrHrefWithParams<string>;
    }

    function onMovieItemClickInternal() {
        // TODO: Should we always track clicks?
        // Currently only movies with (releaseDate > now + 2 weeks) will be tracked
        if (analytics?.track && content?.releaseDate && new Date(content.releaseDate) > dateTodayPlus(14)) {
            track('Movie Poster Clicked', {
                page: currentPageName,
                source: analytics?.track?.sourceName || 'N/A',
                contentId: content.id,
                contentName: content?.title || 'N/A',
                movieReleaseDate: content?.releaseDate || 'N/A'
            });
        }

        onMovieItemClick?.(content, index);
    }

    return (
        <Link
            className={cn('hover:opacity-100 duration-100', {
                'opacity-70': selectedContentItem && !isActiveContentItem
            })}
            draggable={false}
            href={getProgramDetailUrl(content.id)}
            onMouseEnter={() => onMovieItemHover && onMovieItemHover(content, index)}
            onClick={onMovieItemClickInternal}
            /*
             * Prefetch the full page data beyond the loading.js boundaries by setting the prefetch prop to true
             * (See: https://nextjs.org/docs/app/building-your-application/routing/linking-and-navigating#2-prefetching)
             */
            aria-label={title}
            prefetch={false}>
            <div className="flex flex-col space-y-8">
                <div className="relative h-[195px] aspect-[2/3] select-none">
                    <ImageWithTransition
                        className="w-full h-full object-cover rounded-4 cursor-pointer"
                        draggable={false}
                        placeholder="blur"
                        blurDataURL={
                            posterBlurImageUrl
                                ? posterBlurImageUrl
                                : loadAsset('/images/placeholders/portrait/im-posterFilmWithTitlePortrait.jpg')
                        }
                        src={
                            posterImageUrl
                                ? posterImageUrl
                                : loadAsset('/images/placeholders/portrait/im-posterFilmWithTitlePortrait.jpg')
                        }
                        title={title}
                        width={150}
                        height={300}
                        alt={`${title} poster`}
                    />
                    {!posterImageUrl && (
                        <span className="absolute left-0 bottom-0 p-4 w-full text-body-bold-10 text-center opacity-80">
                            {title}
                        </span>
                    )}
                </div>

                {showReleaseDate && (
                    <div className="text-center">
                        <div className=" w-full flex items-center justify-center relative z-10 border-2 border-[var(--surface-color)] rounded-4 p-8">
                            <span className="text-body-bold-12 text-[var(--on-background-color)] leading-[20px]">
                                {formattedReleaseDate
                                    ? t('home.from-date', {
                                          date: format.dateTime.formatDateNumbersOnly(formattedReleaseDate)
                                      })
                                    : t('common.soon')}
                            </span>
                        </div>
                    </div>
                )}
            </div>
        </Link>
    );
}

export default HorizontalMovieListItem;
